import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Calendar, ChevronRight } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { blogAPI } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

export const Blog = () => {
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await blogAPI.getAll(true);
        setPosts(response.data);
      } catch (error) {
        console.error('Error fetching posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  return (
    <Layout>
      {/* Header */}
      <section className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-[#FF5722]/20 rounded-lg">
              <BookOpen className="w-6 h-6 text-[#FF5722]" />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
              Blog
            </h1>
          </div>
          <p className="text-slate-300 text-lg max-w-2xl">
            Dicas, novidades e conteúdos sobre ofertas e promoções.
          </p>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-12 md:py-16 bg-slate-50">
        <div className="container mx-auto px-4 max-w-7xl">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm">
                  <Skeleton className="h-48 w-full" />
                  <div className="p-5 space-y-3">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-6 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nenhum post publicado ainda.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post, index) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 animate-fade-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`blog-card-${post.slug}`}
                >
                  {/* Image */}
                  {post.featured_image && (
                    <div className="aspect-video bg-slate-100 overflow-hidden">
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-5">
                    {/* Meta */}
                    <div className="flex items-center gap-2 text-slate-500 text-sm mb-3">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(post.created_at)}</span>
                      {post.category && (
                        <>
                          <span className="text-slate-300">•</span>
                          <span className="text-[#FF5722]">{post.category}</span>
                        </>
                      )}
                    </div>

                    {/* Title */}
                    <h3 className="font-heading font-semibold text-lg text-slate-800 group-hover:text-[#FF5722] transition-colors mb-2 line-clamp-2">
                      {post.title}
                    </h3>

                    {/* Excerpt */}
                    {post.excerpt && (
                      <p className="text-slate-500 text-sm line-clamp-3 mb-4">
                        {post.excerpt}
                      </p>
                    )}

                    {/* Read more */}
                    <div className="flex items-center gap-1 text-[#FF5722] text-sm font-medium">
                      <span>Ler mais</span>
                      <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Blog;
